/**
 * DecisionFlow — ChannelManager
 * ui/src/components/tenant-admin/ChannelManager.jsx
 *
 * Domain: §P4-06 Channel / Partner
 * Nav route: /admin/channel
 * API:
 *   GET  /api/v1/partner-entities
 *   POST /api/v1/partner-entities
 *   GET  /api/v1/partner-comp-plans?partner_entity_id={id}
 *   POST /api/v1/partner-comp-plans
 *   POST /api/v1/partner-comp-plans/{id}/approve
 *   GET  /api/v1/partner-deal-registrations?partner_entity_id={id}
 *   POST /api/v1/partner-deal-registrations
 *   POST /api/v1/partner-deal-registrations/{id}/approve
 *   GET  /api/v1/territory-partner-assignments?partner_entity_id={id}
 *   POST /api/v1/territory-partner-assignments
 *   POST /api/v1/territory-partner-assignments/{id}/approve
 * Feature gate: Enterprise — channel_compensation
 *   403 FEATURE_UNAVAILABLE → GateStatusBanner.
 * Tabs: Entities | Comp Plans | Deal Registrations | Territory
 * All state: standard (approved_by / submitted_at).
 * Path: ui/src/components/tenant-admin/ChannelManager.jsx
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { useFeatures } from '../../auth/FeatureContext.jsx';
import { apiGet, apiPost } from '../../api/client';
import ApprovalPanel from '../shared/ApprovalPanel.jsx';
import GateStatusBanner from '../shared/GateStatusBanner.jsx';
import StatusPill from '../shared/StatusPill.jsx';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function deriveState(entity) {
  if (entity.approved_by)                         return 'approved';
  if (entity.submitted_at || entity.submitted_by) return 'pending_approval';
  return 'draft';
}

function getApiErrorCode(error) { return error?.code || null; }
function getErrorMessage(error, fallback) { return error?.message || fallback; }

// ---------------------------------------------------------------------------
// CSS tokens
// ---------------------------------------------------------------------------

const thClass      = 'px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]';
const tdClass      = 'px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] border-b border-[rgb(var(--color-border))]';
const btnPrimary   = 'rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-sm font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50 disabled:cursor-not-allowed';
const btnSecondary = 'rounded-lg bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50 disabled:cursor-not-allowed';
const inputClass   = 'mt-1 block w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-2 text-tok-sm focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-focus-ring))]';
const labelClass   = 'flex flex-col text-tok-sm font-medium text-[rgb(var(--color-text))]';

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

const TABS = [
  { key: 'entities',     label: 'Entities'          },
  { key: 'comp-plans',   label: 'Comp Plans'        },
  { key: 'deal-reg',     label: 'Deal Registrations'},
  { key: 'territory',    label: 'Territory'         },
];

// ---------------------------------------------------------------------------
// Form defaults
// ---------------------------------------------------------------------------

const EMPTY_ENTITY = { entity_name: '', entity_type: '', partner_tier: '' };
const EMPTY_PLAN   = { plan_name: '', base_rate_pct: '', effective_date: '', expiration_date: '' };
const EMPTY_DEAL   = { deal_name: '', deal_amount: '', currency: 'USD', registration_date: '' };
const EMPTY_TA     = { territory_id: '', start_date: '', end_date: '' };

// ---------------------------------------------------------------------------
// Reusable sub-list (loading / error / empty / table)
// ---------------------------------------------------------------------------

function SubList({ loading, error, items, columns, rowKey, onRowClick, selectedId, emptyText }) {
  if (loading) return <div className="animate-pulse space-y-2"><div className="h-8 rounded bg-[rgb(var(--color-surface-muted))]" /></div>;
  if (error)   return <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">{error}</div>;
  if (!items.length) return (
    <div className="rounded-xl border border-dashed border-[rgb(var(--color-border))] px-6 py-8 text-center">
      <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">{emptyText}</p>
    </div>
  );
  return (
    <div className="overflow-x-auto rounded-xl border border-[rgb(var(--color-border))]">
      <table className="min-w-full border-collapse">
        <thead><tr>{columns.map((c) => <th key={c.key} className={thClass}>{c.label}</th>)}</tr></thead>
        <tbody>
          {items.map((row) => (
            <tr
              key={row[rowKey]}
              onClick={() => onRowClick && onRowClick(row)}
              className={`${onRowClick ? 'cursor-pointer' : ''} hover:bg-[rgb(var(--color-surface-muted))] transition-colors ${selectedId && row[rowKey] === selectedId ? 'bg-[rgb(var(--color-primary))]/5' : ''}`}
            >
              {columns.map((c, i) => (
                <td key={c.key} className={tdClass + (i === 0 ? ' font-medium' : '')}>
                  {c.render ? c.render(row) : (row[c.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ChannelManager
// ---------------------------------------------------------------------------

export default function ChannelManager({
  mode, period, wizardSteps, embedded = false,
  planId, planComponentId, fiscalPeriodId, readOnly = false,
}) {
  const { user } = useAuth();
  const { hasPlanType } = useFeatures();

  if (!hasPlanType('feature.plan.channel')) {
    return <GateStatusBanner code="PLAN_TYPE_NOT_ENTITLED" />;
  }
  const fpId = period?.fiscal_period_id ?? fiscalPeriodId ?? null;

  const [gated, setGated]               = useState(false);
  const [activeTab, setActiveTab]       = useState('entities');

  // ── Entities ──
  const [entities, setEntities]         = useState([]);
  const [entitiesLoading, setEntitiesLoading] = useState(false);
  const [entitiesError, setEntitiesError]     = useState('');
  const [selectedEntity, setSelectedEntity]   = useState(null);
  const [showEntityForm, setShowEntityForm]   = useState(false);
  const [entityForm, setEntityForm]           = useState(EMPTY_ENTITY);
  const [entitySaving, setEntitySaving]       = useState(false);
  const [entityFormError, setEntityFormError] = useState('');

  // ── Comp Plans ──
  const [plans, setPlans]               = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError]     = useState('');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPlanForm, setShowPlanForm] = useState(false);
  const [planForm, setPlanForm]         = useState(EMPTY_PLAN);
  const [planSaving, setPlanSaving]     = useState(false);
  const [planFormError, setPlanFormError] = useState('');
  const [planActionLoading, setPlanActionLoading] = useState(false);
  const [planActionError, setPlanActionError]     = useState('');

  // ── Deal Registrations ──
  const [deals, setDeals]               = useState([]);
  const [dealsLoading, setDealsLoading] = useState(false);
  const [dealsError, setDealsError]     = useState('');
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [showDealForm, setShowDealForm] = useState(false);
  const [dealForm, setDealForm]         = useState(EMPTY_DEAL);
  const [dealSaving, setDealSaving]     = useState(false);
  const [dealFormError, setDealFormError] = useState('');
  const [dealActionLoading, setDealActionLoading] = useState(false);
  const [dealActionError, setDealActionError]     = useState('');

  // ── Territory Assignments ──
  const [taItems, setTaItems]           = useState([]);
  const [taLoading, setTaLoading]       = useState(false);
  const [taError, setTaError]           = useState('');
  const [selectedTa, setSelectedTa]     = useState(null);
  const [showTaForm, setShowTaForm]     = useState(false);
  const [taForm, setTaForm]             = useState(EMPTY_TA);
  const [taSaving, setTaSaving]         = useState(false);
  const [taFormError, setTaFormError]   = useState('');
  const [taActionLoading, setTaActionLoading] = useState(false);
  const [taActionError, setTaActionError]     = useState('');

  const [toast, setToast] = useState(null);
  const showToast = useCallback((msg) => { setToast(msg); window.setTimeout(() => setToast(null), 3000); }, []);

  // ---------------------------------------------------------------------------
  // Load entities — gate check first
  // ---------------------------------------------------------------------------

  const loadEntities = useCallback(async () => {
    if (!user) return;
    setEntitiesLoading(true); setEntitiesError(''); setGated(false);
    try {
      const data = await apiGet('/api/v1/partner-entities');
      setEntities(Array.isArray(data) ? data : data?.items || data?.data || []);
    } catch (error) {
      const code = getApiErrorCode(error);
      if (code === 'FEATURE_UNAVAILABLE') { setGated(true); }
      else { setEntitiesError(getErrorMessage(error, 'Unable to load partner entities.')); }
    } finally { setEntitiesLoading(false); }
  }, [user]);

  useEffect(() => { loadEntities(); }, [loadEntities]);

  // Sub-list loaders gated on selectedEntity
  const loadPlans = useCallback(async (entityId) => {
    if (!entityId) return;
    setPlansLoading(true); setPlansError('');
    try {
      const data = await apiGet(`/api/v1/partner-comp-plans?partner_entity_id=${entityId}`);
      setPlans(Array.isArray(data) ? data : data?.items || data?.data || []);
    } catch (e) { setPlansError(getErrorMessage(e, 'Unable to load comp plans.')); }
    finally { setPlansLoading(false); }
  }, []);

  const loadDeals = useCallback(async (entityId) => {
    if (!entityId) return;
    setDealsLoading(true); setDealsError('');
    try {
      const data = await apiGet(`/api/v1/partner-deal-registrations?partner_entity_id=${entityId}`);
      setDeals(Array.isArray(data) ? data : data?.items || data?.data || []);
    } catch (e) { setDealsError(getErrorMessage(e, 'Unable to load deal registrations.')); }
    finally { setDealsLoading(false); }
  }, []);

  const loadTa = useCallback(async (entityId) => {
    if (!entityId) return;
    setTaLoading(true); setTaError('');
    try {
      const data = await apiGet(`/api/v1/territory-partner-assignments?partner_entity_id=${entityId}`);
      setTaItems(Array.isArray(data) ? data : data?.items || data?.data || []);
    } catch (e) { setTaError(getErrorMessage(e, 'Unable to load territory assignments.')); }
    finally { setTaLoading(false); }
  }, []);

  useEffect(() => {
    if (!selectedEntity) return;
    const id = selectedEntity.partner_entity_id;
    if (activeTab === 'comp-plans')  loadPlans(id);
    if (activeTab === 'deal-reg')    loadDeals(id);
    if (activeTab === 'territory')   loadTa(id);
  }, [activeTab, selectedEntity, loadPlans, loadDeals, loadTa]);

  // ---------------------------------------------------------------------------
  // Entity create
  // ---------------------------------------------------------------------------

  async function handleCreateEntity(e) {
    e.preventDefault();
    if (!entityForm.entity_name.trim()) { setEntityFormError('Entity name is required.'); return; }
    setEntitySaving(true); setEntityFormError('');
    try {
      await apiPost('/api/v1/partner-entities', {
        entity_name:  entityForm.entity_name.trim(),
        entity_type:  entityForm.entity_type.trim() || null,
        partner_tier: entityForm.partner_tier.trim() || null,
      });
      setShowEntityForm(false); setEntityForm(EMPTY_ENTITY);
      showToast('Partner entity created.');
      await loadEntities();
    } catch (e) { setEntityFormError(getErrorMessage(e, 'Failed to create entity.')); }
    finally { setEntitySaving(false); }
  }

  // ---------------------------------------------------------------------------
  // Comp Plan create + submit/approve
  // ---------------------------------------------------------------------------

  async function handleCreatePlan(e) {
    e.preventDefault();
    if (!selectedEntity) { setPlanFormError('Select an entity first.'); return; }
    if (!planForm.plan_name.trim()) { setPlanFormError('Plan name is required.'); return; }
    setPlanSaving(true); setPlanFormError('');
    try {
      await apiPost('/api/v1/partner-comp-plans', {
        partner_entity_id: selectedEntity.partner_entity_id,
        plan_name:         planForm.plan_name.trim(),
        base_rate_pct:     planForm.base_rate_pct ? parseFloat(planForm.base_rate_pct) : null,
        effective_date:    planForm.effective_date || null,
        expiration_date:   planForm.expiration_date || null,
      });
      setShowPlanForm(false); setPlanForm(EMPTY_PLAN);
      showToast('Comp plan created.');
      await loadPlans(selectedEntity.partner_entity_id);
    } catch (e) { setPlanFormError(getErrorMessage(e, 'Failed to create comp plan.')); }
    finally { setPlanSaving(false); }
  }

  async function handlePlanSubmit() {
    if (!selectedPlan) return;
    setPlanActionLoading(true); setPlanActionError('');
    try {
      await apiPost(`/api/v1/partner-comp-plans/${selectedPlan.partner_comp_plan_id}/submit`);
      showToast('Submitted for approval.');
      await loadPlans(selectedEntity.partner_entity_id);
      setSelectedPlan((p) => ({ ...p, submitted_at: new Date().toISOString() }));
    } catch (e) { setPlanActionError(getErrorMessage(e, 'Failed to submit.')); }
    finally { setPlanActionLoading(false); }
  }

  async function handlePlanApprove() {
    if (!selectedPlan) return;
    setPlanActionLoading(true); setPlanActionError('');
    try {
      await apiPost(`/api/v1/partner-comp-plans/${selectedPlan.partner_comp_plan_id}/approve`);
      showToast('Comp plan approved.');
      await loadPlans(selectedEntity.partner_entity_id);
      setSelectedPlan((p) => ({ ...p, approved_by: user?.user_id || user?.id }));
    } catch (e) {
      const code = getApiErrorCode(e);
      setPlanActionError(code === 'SELF_APPROVAL_NOT_ALLOWED' ? 'Self-approval is not permitted (NN-10).' : getErrorMessage(e, 'Failed to approve.'));
    } finally { setPlanActionLoading(false); }
  }

  // ---------------------------------------------------------------------------
  // Deal Registration create + submit/approve
  // ---------------------------------------------------------------------------

  async function handleCreateDeal(e) {
    e.preventDefault();
    if (!selectedEntity) { setDealFormError('Select an entity first.'); return; }
    if (!dealForm.deal_name.trim()) { setDealFormError('Deal name is required.'); return; }
    setDealSaving(true); setDealFormError('');
    try {
      await apiPost('/api/v1/partner-deal-registrations', {
        partner_entity_id:  selectedEntity.partner_entity_id,
        deal_name:          dealForm.deal_name.trim(),
        deal_amount:        dealForm.deal_amount ? parseFloat(dealForm.deal_amount) : null,
        currency:           dealForm.currency || 'USD',
        registration_date:  dealForm.registration_date || null,
      });
      setShowDealForm(false); setDealForm(EMPTY_DEAL);
      showToast('Deal registration created.');
      await loadDeals(selectedEntity.partner_entity_id);
    } catch (e) { setDealFormError(getErrorMessage(e, 'Failed to create deal registration.')); }
    finally { setDealSaving(false); }
  }

  async function handleDealSubmit() {
    if (!selectedDeal) return;
    setDealActionLoading(true); setDealActionError('');
    try {
      await apiPost(`/api/v1/partner-deal-registrations/${selectedDeal.deal_registration_id}/submit`);
      showToast('Submitted for approval.');
      await loadDeals(selectedEntity.partner_entity_id);
      setSelectedDeal((d) => ({ ...d, submitted_at: new Date().toISOString() }));
    } catch (e) { setDealActionError(getErrorMessage(e, 'Failed to submit.')); }
    finally { setDealActionLoading(false); }
  }

  async function handleDealApprove() {
    if (!selectedDeal) return;
    setDealActionLoading(true); setDealActionError('');
    try {
      await apiPost(`/api/v1/partner-deal-registrations/${selectedDeal.deal_registration_id}/approve`);
      showToast('Deal approved.');
      await loadDeals(selectedEntity.partner_entity_id);
      setSelectedDeal((d) => ({ ...d, approved_by: user?.user_id || user?.id }));
    } catch (e) {
      const code = getApiErrorCode(e);
      setDealActionError(code === 'SELF_APPROVAL_NOT_ALLOWED' ? 'Self-approval is not permitted (NN-10).' : getErrorMessage(e, 'Failed to approve.'));
    } finally { setDealActionLoading(false); }
  }

  // ---------------------------------------------------------------------------
  // Territory Assignment create + submit/approve
  // ---------------------------------------------------------------------------

  async function handleCreateTa(e) {
    e.preventDefault();
    if (!selectedEntity) { setTaFormError('Select an entity first.'); return; }
    if (!taForm.territory_id.trim()) { setTaFormError('Territory ID is required.'); return; }
    setTaSaving(true); setTaFormError('');
    try {
      await apiPost('/api/v1/territory-partner-assignments', {
        partner_entity_id: selectedEntity.partner_entity_id,
        territory_id:      taForm.territory_id.trim(),
        start_date:        taForm.start_date || null,
        end_date:          taForm.end_date || null,
      });
      setShowTaForm(false); setTaForm(EMPTY_TA);
      showToast('Territory assignment created.');
      await loadTa(selectedEntity.partner_entity_id);
    } catch (e) { setTaFormError(getErrorMessage(e, 'Failed to create territory assignment.')); }
    finally { setTaSaving(false); }
  }

  async function handleTaSubmit() {
    if (!selectedTa) return;
    setTaActionLoading(true); setTaActionError('');
    try {
      await apiPost(`/api/v1/territory-partner-assignments/${selectedTa.territory_partner_assignment_id}/submit`);
      showToast('Submitted for approval.');
      await loadTa(selectedEntity.partner_entity_id);
      setSelectedTa((t) => ({ ...t, submitted_at: new Date().toISOString() }));
    } catch (e) { setTaActionError(getErrorMessage(e, 'Failed to submit.')); }
    finally { setTaActionLoading(false); }
  }

  async function handleTaApprove() {
    if (!selectedTa) return;
    setTaActionLoading(true); setTaActionError('');
    try {
      await apiPost(`/api/v1/territory-partner-assignments/${selectedTa.territory_partner_assignment_id}/approve`);
      showToast('Assignment approved.');
      await loadTa(selectedEntity.partner_entity_id);
      setSelectedTa((t) => ({ ...t, approved_by: user?.user_id || user?.id }));
    } catch (e) {
      const code = getApiErrorCode(e);
      setTaActionError(code === 'SELF_APPROVAL_NOT_ALLOWED' ? 'Self-approval is not permitted (NN-10).' : getErrorMessage(e, 'Failed to approve.'));
    } finally { setTaActionLoading(false); }
  }

  // ---------------------------------------------------------------------------
  // Guards
  // ---------------------------------------------------------------------------

  const currentUserId = user?.user_id || user?.id || null;

  if (!user) return (
    <section className="flex flex-col gap-4">
      <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">Authentication required.</div>
    </section>
  );

  if (gated) return (
    <section className="flex flex-col gap-4">
      <GateStatusBanner feature="channel_compensation" />
    </section>
  );

  function NoEntitySelected() {
    return (
      <div className="rounded-lg border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/5 px-4 py-3 text-tok-sm text-[rgb(var(--color-warning))]">
        Select a partner entity in the Entities tab first.
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <section className="flex flex-col gap-4">
      <h1 className="text-tok-lg font-bold text-[rgb(var(--color-text))]">Channel / Partner</h1>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-[rgb(var(--color-border))] overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.key} type="button" onClick={() => setActiveTab(t.key)}
            className={`whitespace-nowrap px-4 py-2 text-tok-sm font-medium border-b-2 transition ${
              activeTab === t.key
                ? 'border-[rgb(var(--color-primary))] text-[rgb(var(--color-primary))]'
                : 'border-transparent text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Entities tab ── */}
      {activeTab === 'entities' && (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Partner Entities</h2>
            {!readOnly && (
              <button type="button" onClick={() => { setShowEntityForm((v) => !v); setEntityFormError(''); setEntityForm(EMPTY_ENTITY); }} className={btnPrimary}>
                {showEntityForm ? 'Cancel' : '+ Add Entity'}
              </button>
            )}
          </div>
          {showEntityForm && (
            <form onSubmit={handleCreateEntity} className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 flex flex-col gap-3">
              <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">New Partner Entity</p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <label className={labelClass}>
                  Entity Name <span className="text-[rgb(var(--color-danger))]">*</span>
                  <input type="text" value={entityForm.entity_name} onChange={(e) => setEntityForm((f) => ({ ...f, entity_name: e.target.value }))} placeholder="e.g. Acme Reseller" className={inputClass} required />
                </label>
                <label className={labelClass}>
                  Entity Type
                  <input type="text" value={entityForm.entity_type} onChange={(e) => setEntityForm((f) => ({ ...f, entity_type: e.target.value }))} placeholder="e.g. reseller, distributor" className={inputClass} />
                </label>
                <label className={labelClass}>
                  Partner Tier
                  <input type="text" value={entityForm.partner_tier} onChange={(e) => setEntityForm((f) => ({ ...f, partner_tier: e.target.value }))} placeholder="e.g. gold, silver" className={inputClass} />
                </label>
              </div>
              {entityFormError && <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{entityFormError}</div>}
              <div className="flex gap-2">
                <button type="submit" disabled={entitySaving} className={btnPrimary}>{entitySaving ? 'Saving…' : 'Create Entity'}</button>
                <button type="button" onClick={() => { setShowEntityForm(false); setEntityFormError(''); }} className={btnSecondary}>Cancel</button>
              </div>
            </form>
          )}
          <SubList
            loading={entitiesLoading} error={entitiesError} items={entities}
            rowKey="partner_entity_id" selectedId={selectedEntity?.partner_entity_id}
            emptyText="No partner entities. Add one to begin channel compensation setup."
            onRowClick={(row) => setSelectedEntity(selectedEntity?.partner_entity_id === row.partner_entity_id ? null : row)}
            columns={[
              { key: 'entity_name', label: 'Name' },
              { key: 'entity_type', label: 'Type' },
              { key: 'partner_tier', label: 'Tier' },
            ]}
          />
          {selectedEntity && <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Selected: <strong className="text-[rgb(var(--color-text))]">{selectedEntity.entity_name}</strong> — switch tabs to manage comp plans, deals, or territory.</p>}
        </div>
      )}

      {/* ── Comp Plans tab ── */}
      {activeTab === 'comp-plans' && (
        <div className="flex flex-col gap-4">
          {!selectedEntity ? <NoEntitySelected /> : (
            <>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Comp Plans — {selectedEntity.entity_name}</h2>
                {!readOnly && <button type="button" onClick={() => { setShowPlanForm((v) => !v); setPlanFormError(''); setPlanForm(EMPTY_PLAN); }} className={btnPrimary}>{showPlanForm ? 'Cancel' : '+ Add Plan'}</button>}
              </div>
              {showPlanForm && (
                <form onSubmit={handleCreatePlan} className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 flex flex-col gap-3">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <label className={labelClass + ' sm:col-span-2'}>Plan Name <span className="text-[rgb(var(--color-danger))]">*</span><input type="text" value={planForm.plan_name} onChange={(e) => setPlanForm((f) => ({ ...f, plan_name: e.target.value }))} placeholder="e.g. Gold Reseller Q1" className={inputClass} required /></label>
                    <label className={labelClass}>Base Rate %<input type="number" step="0.01" min="0" value={planForm.base_rate_pct} onChange={(e) => setPlanForm((f) => ({ ...f, base_rate_pct: e.target.value }))} placeholder="0.00" className={inputClass} /></label>
                    <div />
                    <label className={labelClass}>Effective Date<input type="date" value={planForm.effective_date} onChange={(e) => setPlanForm((f) => ({ ...f, effective_date: e.target.value }))} className={inputClass} /></label>
                    <label className={labelClass}>Expiration Date<input type="date" value={planForm.expiration_date} onChange={(e) => setPlanForm((f) => ({ ...f, expiration_date: e.target.value }))} className={inputClass} /></label>
                  </div>
                  {planFormError && <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{planFormError}</div>}
                  <div className="flex gap-2"><button type="submit" disabled={planSaving} className={btnPrimary}>{planSaving ? 'Saving…' : 'Create Plan'}</button><button type="button" onClick={() => { setShowPlanForm(false); setPlanFormError(''); }} className={btnSecondary}>Cancel</button></div>
                </form>
              )}
              <SubList loading={plansLoading} error={plansError} items={plans} rowKey="partner_comp_plan_id" selectedId={selectedPlan?.partner_comp_plan_id}
                emptyText="No comp plans for this partner. Add one above."
                onRowClick={(row) => { setSelectedPlan(row); setPlanActionError(''); }}
                columns={[
                  { key: 'plan_name', label: 'Plan Name' },
                  { key: 'base_rate_pct', label: 'Base Rate', render: (r) => r.base_rate_pct != null ? `${r.base_rate_pct}%` : '—' },
                  { key: 'effective_date', label: 'Effective' },
                  { key: '_status', label: 'Status', render: (r) => <StatusPill status={deriveState(r)} /> },
                ]}
              />
              {selectedPlan && !readOnly && (() => {
                const state = deriveState(selectedPlan);
                return (
                  <div className="flex flex-col gap-3">
                    {planActionError && <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{planActionError}</div>}
                    <ApprovalPanel variant="partner_comp_plan" currentState={state} submittedBy={selectedPlan.submitted_by} currentUserId={currentUserId}
                      onSubmit={state === 'draft' ? handlePlanSubmit : null} onApprove={state === 'pending_approval' ? handlePlanApprove : null} onReject={null}
                      approvedBy={selectedPlan.approved_by} approvedAt={selectedPlan.approved_at} loading={planActionLoading} />
                  </div>
                );
              })()}
            </>
          )}
        </div>
      )}

      {/* ── Deal Registrations tab ── */}
      {activeTab === 'deal-reg' && (
        <div className="flex flex-col gap-4">
          {!selectedEntity ? <NoEntitySelected /> : (
            <>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Deal Registrations — {selectedEntity.entity_name}</h2>
                {!readOnly && <button type="button" onClick={() => { setShowDealForm((v) => !v); setDealFormError(''); setDealForm(EMPTY_DEAL); }} className={btnPrimary}>{showDealForm ? 'Cancel' : '+ Register Deal'}</button>}
              </div>
              {showDealForm && (
                <form onSubmit={handleCreateDeal} className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 flex flex-col gap-3">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <label className={labelClass + ' sm:col-span-2'}>Deal Name <span className="text-[rgb(var(--color-danger))]">*</span><input type="text" value={dealForm.deal_name} onChange={(e) => setDealForm((f) => ({ ...f, deal_name: e.target.value }))} placeholder="e.g. Acme Q2 Enterprise" className={inputClass} required /></label>
                    <label className={labelClass}>Deal Amount<input type="number" step="0.01" min="0" value={dealForm.deal_amount} onChange={(e) => setDealForm((f) => ({ ...f, deal_amount: e.target.value }))} placeholder="0.00" className={inputClass} /></label>
                    <label className={labelClass}>Currency<input type="text" maxLength={3} value={dealForm.currency} onChange={(e) => setDealForm((f) => ({ ...f, currency: e.target.value.toUpperCase() }))} placeholder="USD" className={inputClass} /></label>
                    <label className={labelClass}>Registration Date<input type="date" value={dealForm.registration_date} onChange={(e) => setDealForm((f) => ({ ...f, registration_date: e.target.value }))} className={inputClass} /></label>
                  </div>
                  {dealFormError && <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{dealFormError}</div>}
                  <div className="flex gap-2"><button type="submit" disabled={dealSaving} className={btnPrimary}>{dealSaving ? 'Saving…' : 'Register Deal'}</button><button type="button" onClick={() => { setShowDealForm(false); setDealFormError(''); }} className={btnSecondary}>Cancel</button></div>
                </form>
              )}
              <SubList loading={dealsLoading} error={dealsError} items={deals} rowKey="deal_registration_id" selectedId={selectedDeal?.deal_registration_id}
                emptyText="No deal registrations for this partner."
                onRowClick={(row) => { setSelectedDeal(row); setDealActionError(''); }}
                columns={[
                  { key: 'deal_name', label: 'Deal Name' },
                  { key: 'deal_amount', label: 'Amount', render: (r) => r.deal_amount != null ? Number(r.deal_amount).toLocaleString() : '—' },
                  { key: 'currency', label: 'Currency' },
                  { key: 'registration_date', label: 'Registered' },
                  { key: '_status', label: 'Status', render: (r) => <StatusPill status={deriveState(r)} /> },
                ]}
              />
              {selectedDeal && !readOnly && (() => {
                const state = deriveState(selectedDeal);
                return (
                  <div className="flex flex-col gap-3">
                    {dealActionError && <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{dealActionError}</div>}
                    <ApprovalPanel variant="deal_registration" currentState={state} submittedBy={selectedDeal.submitted_by} currentUserId={currentUserId}
                      onSubmit={state === 'draft' ? handleDealSubmit : null} onApprove={state === 'pending_approval' ? handleDealApprove : null} onReject={null}
                      approvedBy={selectedDeal.approved_by} approvedAt={selectedDeal.approved_at} loading={dealActionLoading} />
                  </div>
                );
              })()}
            </>
          )}
        </div>
      )}

      {/* ── Territory tab ── */}
      {activeTab === 'territory' && (
        <div className="flex flex-col gap-4">
          {!selectedEntity ? <NoEntitySelected /> : (
            <>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Territory Assignments — {selectedEntity.entity_name}</h2>
                {!readOnly && <button type="button" onClick={() => { setShowTaForm((v) => !v); setTaFormError(''); setTaForm(EMPTY_TA); }} className={btnPrimary}>{showTaForm ? 'Cancel' : '+ Add Territory'}</button>}
              </div>
              {showTaForm && (
                <form onSubmit={handleCreateTa} className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 flex flex-col gap-3">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <label className={labelClass}>Territory ID <span className="text-[rgb(var(--color-danger))]">*</span><input type="text" value={taForm.territory_id} onChange={(e) => setTaForm((f) => ({ ...f, territory_id: e.target.value }))} placeholder="e.g. TERR-001" className={inputClass} required /></label>
                    <label className={labelClass}>Start Date<input type="date" value={taForm.start_date} onChange={(e) => setTaForm((f) => ({ ...f, start_date: e.target.value }))} className={inputClass} /></label>
                    <label className={labelClass}>End Date<input type="date" value={taForm.end_date} onChange={(e) => setTaForm((f) => ({ ...f, end_date: e.target.value }))} className={inputClass} /></label>
                  </div>
                  {taFormError && <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{taFormError}</div>}
                  <div className="flex gap-2"><button type="submit" disabled={taSaving} className={btnPrimary}>{taSaving ? 'Saving…' : 'Add Territory'}</button><button type="button" onClick={() => { setShowTaForm(false); setTaFormError(''); }} className={btnSecondary}>Cancel</button></div>
                </form>
              )}
              <SubList loading={taLoading} error={taError} items={taItems} rowKey="territory_partner_assignment_id" selectedId={selectedTa?.territory_partner_assignment_id}
                emptyText="No territory assignments for this partner."
                onRowClick={(row) => { setSelectedTa(row); setTaActionError(''); }}
                columns={[
                  { key: 'territory_id', label: 'Territory' },
                  { key: 'start_date', label: 'Start' },
                  { key: 'end_date', label: 'End' },
                  { key: '_status', label: 'Status', render: (r) => <StatusPill status={deriveState(r)} /> },
                ]}
              />
              {selectedTa && !readOnly && (() => {
                const state = deriveState(selectedTa);
                return (
                  <div className="flex flex-col gap-3">
                    {taActionError && <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{taActionError}</div>}
                    <ApprovalPanel variant="territory_partner_assignment" currentState={state} submittedBy={selectedTa.submitted_by} currentUserId={currentUserId}
                      onSubmit={state === 'draft' ? handleTaSubmit : null} onApprove={state === 'pending_approval' ? handleTaApprove : null} onReject={null}
                      approvedBy={selectedTa.approved_by} approvedAt={selectedTa.approved_at} loading={taActionLoading} />
                  </div>
                );
              })()}
            </>
          )}
        </div>
      )}

      {toast && (
        <div role="status" className="fixed bottom-4 right-4 z-50 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-3 text-tok-sm text-[rgb(var(--color-text))] shadow-lg">{toast}</div>
      )}
    </section>
  );
}
